'use client';

import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  RegisterToolbar,
  type RegisterExportAction,
  type RegisterFilter,
} from '../register-toolbar';
import { DataGridFilterControls } from './data-grid-filter-controls';
import { getActiveFilterChips, useDebouncedGridSearch } from './data-grid-toolbar';
import type { ActiveFilters, FilterSpec } from './types';

export interface DataGridRegisterToolbarProps {
  title?: string;
  totalCount: number;
  countLabel?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filters?: ReadonlyArray<FilterSpec>;
  activeFilters?: ActiveFilters;
  onFilterChange?: (filterId: string, value: unknown) => void;
  onFilterClear?: (filterId: string) => void;
  onClearAllFilters?: () => void;
  columnControlsSlot?: React.ReactNode;
  savedViewsSlot?: React.ReactNode;
  moreControls?: React.ReactNode;
  primaryControls?: React.ReactNode;
  actions?: React.ReactNode;
  toolbar?: React.ReactNode;
  onExportRequest?: () => void;
  registerToolbar?: {
    ariaLabel?: string;
    inlineFilters?: React.ReactNode;
    exports?: readonly RegisterExportAction[];
    showTitle?: boolean;
  };
}

export function DataGridRegisterToolbar({
  title,
  totalCount,
  countLabel,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filters,
  activeFilters,
  onFilterChange,
  onFilterClear,
  onClearAllFilters,
  columnControlsSlot,
  savedViewsSlot,
  moreControls,
  primaryControls,
  actions,
  toolbar,
  onExportRequest,
  registerToolbar,
}: DataGridRegisterToolbarProps) {
  const { localSearch, handleSearchChange, clearSearch } = useDebouncedGridSearch(
    searchValue,
    onSearchChange,
  );
  const chips = getActiveFilterChips(filters, activeFilters);
  const registerFilters: RegisterFilter[] = (filters ?? []).map((spec) => ({
    id: spec.id,
    label: spec.label,
    active: chips.some((chip) => chip.id === spec.id),
    control: onFilterChange ? (
      <DataGridFilterControls
        filters={[spec]}
        activeFilters={activeFilters ?? {}}
        onFilterChange={onFilterChange}
      />
    ) : null,
  }));
  const exports: readonly RegisterExportAction[] = [
    ...(onExportRequest
      ? [{ id: 'grid-csv', label: 'Export CSV', onSelect: onExportRequest }]
      : []),
    ...(registerToolbar?.exports ?? []),
  ];
  const tools =
    savedViewsSlot || moreControls || toolbar || actions ? (
      <>
        {savedViewsSlot}
        {moreControls}
        {toolbar}
        {actions}
      </>
    ) : undefined;
  const inlineFilters =
    primaryControls || registerToolbar?.inlineFilters ? (
      <>
        {primaryControls}
        {registerToolbar?.inlineFilters}
      </>
    ) : undefined;

  return (
    <div
      className="flex min-w-0 flex-col gap-2 border-b border-border bg-surface-sheet px-4 py-3"
      data-data-grid-register-toolbar
    >
      {title && registerToolbar?.showTitle !== false && (
        <h2 className="min-w-0 break-words font-body text-section-title font-semibold text-text-heading">
          {title}
        </h2>
      )}
      <RegisterToolbar
        ariaLabel={title ?? registerToolbar?.ariaLabel}
        search={onSearchChange ? localSearch : undefined}
        onSearchChange={onSearchChange ? handleSearchChange : undefined}
        searchLabel={searchPlaceholder ?? 'Search…'}
        filters={registerFilters}
        inlineFilters={inlineFilters}
        columns={columnControlsSlot}
        tools={tools}
        exports={exports}
        count={
          countLabel ??
          `${totalCount.toLocaleString('en-AU')} ${totalCount === 1 ? 'result' : 'results'}`
        }
        onClear={
          onClearAllFilters || (onSearchChange && chips.length === 0)
            ? () => {
                clearSearch();
                onClearAllFilters?.();
              }
            : undefined
        }
      />
      {chips.length > 0 && (
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {chips.map((chip) =>
            onFilterClear ? (
              <button
                key={chip.id}
                type="button"
                onClick={() => onFilterClear(chip.id)}
                aria-label={`Clear filter ${chip.label}`}
                className={cn(
                  'inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-statura-green-accessible/30 bg-statura-green-accessible/10 px-2 py-0.5 text-left text-[10.5px] font-medium text-statura-green-accessible whitespace-nowrap break-normal [overflow-wrap:normal]',
                  'hover:bg-statura-green-accessible/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible',
                )}
              >
                {chip.label}
                <X className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
              </button>
            ) : (
              <span
                key={chip.id}
                className="inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border border-statura-green-accessible/30 bg-statura-green-accessible/10 px-2 py-0.5 text-[10.5px] font-medium text-statura-green-accessible whitespace-normal break-words [overflow-wrap:anywhere]"
              >
                {chip.label}
              </span>
            ),
          )}
        </div>
      )}
    </div>
  );
}
